import {
  IconDownload as Download,
  IconFile as FileIcon,
  IconFileText as FileText,
  IconMovie as Movie,
  IconPhoto as Photo,
  IconX as X,
} from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { formatFileSize } from "./attachments";
import {
  fetchTextAttachmentPreview,
  attachmentPresentationKind,
  type MessageAttachmentView,
  type TextPreview,
} from "./messageAttachmentPreview";

interface MessageAttachmentsProps {
  /**
   * 旧版实时事件可能没有 attachments 字段。这里必须把它当成无附件消息，不能让一条
   * 历史事件击穿整个 React 根节点；服务端契约校验仍会负责新数据的严格性。
   */
  attachments?: MessageAttachmentView[];
}

interface OpenPreview {
  attachment: MessageAttachmentView;
  text: TextPreview | null;
  loading: boolean;
  error: string | null;
}

function attachmentIcon(kind: ReturnType<typeof attachmentPresentationKind>) {
  if (kind === "image") return <Photo size={18} />;
  if (kind === "video") return <Movie size={18} />;
  if (kind === "text") return <FileText size={18} />;
  return <FileIcon size={18} />;
}

function AttachmentIdentity({ attachment }: { attachment: MessageAttachmentView }) {
  return (
    <span className="message-attachment-identity">
      <strong title={attachment.filename}>{attachment.filename}</strong>
      <small>
        {attachment.mimeType} · {formatFileSize(attachment.sizeBytes)}
      </small>
    </span>
  );
}

export function MessageAttachments({ attachments = [] }: MessageAttachmentsProps) {
  const [preview, setPreview] = useState<OpenPreview | null>(null);
  const previewRequest = useRef(0);

  const closePreview = () => {
    previewRequest.current += 1;
    setPreview(null);
  };

  const openPreview = async (attachment: MessageAttachmentView) => {
    const kind = attachmentPresentationKind(attachment);
    if (!attachment.available || !attachment.contentUrl || kind === "other") return;
    const request = previewRequest.current + 1;
    previewRequest.current = request;
    setPreview({ attachment, text: null, loading: kind === "text", error: null });
    if (kind !== "text") return;
    try {
      const text = await fetchTextAttachmentPreview(attachment.contentUrl);
      if (previewRequest.current === request) {
        setPreview({ attachment, text, loading: false, error: null });
      }
    } catch (caught) {
      if (previewRequest.current === request) {
        setPreview({
          attachment,
          text: null,
          loading: false,
          error: caught instanceof Error ? caught.message : "附件预览加载失败",
        });
      }
    }
  };

  useEffect(() => {
    if (!preview) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closePreview();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [preview]);

  if (!attachments.length) return null;

  return (
    <>
      <div className="message-attachments" aria-label="已发送附件">
        {attachments.map((attachment) => {
          const kind = attachmentPresentationKind(attachment);
          const previewable =
            attachment.available && Boolean(attachment.contentUrl) && kind !== "other";
          return (
            <article
              className={`message-attachment message-attachment--${kind}${attachment.available ? "" : " message-attachment--unavailable"}`}
              key={attachment.attachmentId}
            >
              {previewable ? (
                <button
                  type="button"
                  className="message-attachment-open"
                  aria-label={`预览附件：${attachment.filename}`}
                  onClick={() => void openPreview(attachment)}
                >
                  {kind === "image" ? (
                    <img src={attachment.contentUrl!} alt="" loading="lazy" />
                  ) : (
                    <span className="message-attachment-icon" aria-hidden="true">
                      {attachmentIcon(kind)}
                    </span>
                  )}
                  <AttachmentIdentity attachment={attachment} />
                </button>
              ) : (
                <div className="message-attachment-static">
                  <span className="message-attachment-icon" aria-hidden="true">
                    {attachmentIcon(kind)}
                  </span>
                  <AttachmentIdentity attachment={attachment} />
                </div>
              )}
              {attachment.available && attachment.downloadUrl ? (
                <a
                  className="message-attachment-download"
                  href={attachment.downloadUrl}
                  aria-label={`下载附件：${attachment.filename}`}
                  title="下载附件"
                >
                  <Download size={16} aria-hidden="true" />
                </a>
              ) : (
                <span className="message-attachment-unavailable" title="服务器上的附件副本已不可用">
                  不可用
                </span>
              )}
            </article>
          );
        })}
      </div>

      {preview ? (
        <div
          className="attachment-preview-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closePreview();
          }}
        >
          <section
            className="attachment-preview-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="attachment-preview-title"
          >
            <header>
              <div>
                <span className="section-kicker">附件预览</span>
                <h2 id="attachment-preview-title">{preview.attachment.filename}</h2>
              </div>
              <div className="attachment-preview-actions">
                {preview.attachment.downloadUrl ? (
                  <a href={preview.attachment.downloadUrl}>下载原文件</a>
                ) : null}
                <button type="button" aria-label="关闭附件预览" autoFocus onClick={closePreview}>
                  <X size={19} />
                </button>
              </div>
            </header>
            <div className="attachment-preview-content">
              {attachmentPresentationKind(preview.attachment) === "image" ? (
                <img src={preview.attachment.contentUrl!} alt={preview.attachment.filename} />
              ) : null}
              {attachmentPresentationKind(preview.attachment) === "video" ? (
                <video src={preview.attachment.contentUrl!} controls preload="metadata">
                  浏览器无法播放此视频，请下载原文件。
                </video>
              ) : null}
              {attachmentPresentationKind(preview.attachment) === "text" ? (
                preview.loading ? (
                  <p className="attachment-preview-state" role="status">
                    正在加载文本预览…
                  </p>
                ) : preview.error ? (
                  <p
                    className="attachment-preview-state attachment-preview-state--error"
                    role="alert"
                  >
                    {preview.error}
                  </p>
                ) : (
                  <div className="attachment-text-preview" aria-label="只读文本预览">
                    {preview.text?.lines.map((line, index) => (
                      <div className="attachment-text-line" key={`${index}-${line.length}`}>
                        <span aria-hidden="true">{index + 1}</span>
                        <code>{line || " "}</code>
                      </div>
                    ))}
                    {preview.text?.truncated ? (
                      <p className="attachment-preview-state">
                        文件行数较多，仅显示前 5000 行；完整内容请下载。
                      </p>
                    ) : null}
                  </div>
                )
              ) : null}
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
}
